/**
 * Drawing one layer, whatever kind it is, and letting it be dragged.
 *
 * Every layer is a Group placed at its own position, with the drawing inside it
 * starting at the origin. That uniformity is what makes dragging one piece of
 * code rather than eight: an ellipse anchored at its centre and a text box
 * anchored at its top-left would otherwise each need their own arithmetic.
 *
 * Types that need work still to come - images with no asset pipeline, QR with
 * no encoder - draw an honest frame that says what will be there rather than
 * nothing at all. A customer dragging an empty picture box should be able to
 * see the box.
 */

import { useCallback, useRef } from 'react';
import { Circle, Group, Path, Rect, Shape, Text } from 'react-konva';
import type Konva from 'konva';
import type {
  BarcodeLayer,
  GraphicLayer,
  GroupLayer,
  ImageLayer,
  Layer,
  PlaceholderLayer,
  QrLayer,
  ShapeLayer,
  SvgLayer,
} from '../model/types';
import { isGradient } from '../model/types';
import type { Gradient } from '../model/types';
import { mmToPx, pxToMm, roundMm, toCss } from '../model/units';
import { firstColour, graphicPaths, scalePath } from '../press/draw';
import { cornerOf, frameOf, middleOf } from './frame';
import { shapeDef, shapePath } from '../shapes/library';
import { caretAt } from '../text/layout';
import { plan } from '../effects/plan';
import type { Matrix } from '../text/place';
import { paintPasses, pathFor } from './paint';
import { carry, useEditor } from '../state/store';
import { snap } from './snapping';
import { TextNode } from './TextNode';
import { ImageNode } from './ImageNode';
import { QrNode } from './QrNode';

interface Props {
  layer: Layer;
  zoom: number;
}

export function LayerNode({ layer, zoom }: Props) {
  const node = useRef<Konva.Group>(null);
  const others = useRef<Array<{ node: Konva.Node; x: number; y: number }>>([]);
  const origin = useRef({ x: 0, y: 0 });

  const preview = useEditor((s) => s.view.preview);

  /**
   * Picks up the layer, and everything else selected with it.
   *
   * The other selected nodes are moved by hand during the drag rather than
   * left behind to jump at the end - a multiple selection that only partly
   * moves while you are looking at it reads as a bug.
   */
  const onDragStart = useCallback(() => {
    const store = useEditor.getState();
    const group = node.current;

    if (!group) return;

    if (!store.selection.includes(layer.id)) store.select([layer.id]);

    origin.current = { x: group.x(), y: group.y() };

    const stage = group.getStage();

    others.current = stage
      ? useEditor
          .getState()
          .selection.filter((id) => id !== layer.id)
          .map((id) => stage.findOne(`.${id}`))
          .filter((other): other is Konva.Node => !!other)
          .map((other) => ({ node: other, x: other.x(), y: other.y() }))
      : [];
  }, [layer.id]);

  const onDragMove = useCallback(() => {
    const store = useEditor.getState();
    const group = node.current;
    const page = store.doc?.pages[store.pageIndex];

    if (!group || !page || !store.doc) return;

    // The node is placed by its middle, so its position is not the layer's
    // corner - and snapping is entirely about corners and edges.
    const corner = cornerOf(group, layer, zoom);

    const proposed = {
      x: pxToMm(corner.x, zoom),
      y: pxToMm(corner.y, zoom),
      w: layer.w,
      h: layer.h,
    };

    const moving = new Set(store.selection);
    const result = snap(proposed, page, store.doc.safe, zoom, moving);
    const put = middleOf(mmToPx(result.x, zoom), mmToPx(result.y, zoom), layer, zoom);

    group.x(put.x);
    group.y(put.y);

    const dx = group.x() - origin.current.x;
    const dy = group.y() - origin.current.y;

    for (const other of others.current) {
      other.node.x(other.x + dx);
      other.node.y(other.y + dy);
    }

    store.setGuides(result.guides);
  }, [layer.w, layer.h, zoom]);

  const onDragEnd = useCallback(() => {
    const store = useEditor.getState();
    const group = node.current;

    if (!group) return;

    const dx = pxToMm(group.x() - origin.current.x, zoom);
    const dy = pxToMm(group.y() - origin.current.y, zoom);

    // One history entry for the whole gesture, covering every layer that moved.
    store.commit('Move', (doc) => {
      const page = doc.pages[store.pageIndex];

      if (!page) return doc;

      const moving = new Set(store.selection);
      const pages = doc.pages.slice();

      pages[store.pageIndex] = {
        ...page,
        /*
         * carry() because a group keeps its children in the page's own
         * millimetres: a group dragged without it moved its box and left
         * everything inside standing where it was. updateLayer does this for
         * every other edit; this commit writes the page itself, so it has to
         * do it too.
         */
        layers: page.layers.map((other) =>
          moving.has(other.id)
            ? carry(other, { ...other, x: roundMm(other.x + dx), y: roundMm(other.y + dy) })
            : other
        ),
      };

      return { ...doc, pages };
    });

    store.setGuides([]);
    others.current = [];
  }, [zoom]);

  /**
   * Double-click opens whatever this layer's own gesture is: typing for text,
   * cropping for a picture, its content for a code.
   *
   * Not single-click: a customer moving a text box should not have to be
   * careful about where they let go of it.
   */
  const onDblClick = useCallback(() => {
    if (layer.locked) return;

    // A drawing's gesture is its points.
    if (layer.type === 'shape' && layer.shape === 'free') {
      useEditor.getState().beginShaping(layer.id);

      return;
    }

    if (layer.type === 'image') {
      if (layer.asset) useEditor.getState().beginCrop(layer.id, layer.crop);

      return;
    }

    // A code's gesture is what it holds, which lives in a panel.
    if (layer.type === 'qr') {
      useEditor.getState().askPanel('settings');

      return;
    }

    if (layer.type !== 'text') return;

    const group = node.current;
    const local = group?.getRelativePointerPosition();

    const index =
      local === null || local === undefined
        ? null
        : caretAt(layer, { x: pxToMm(local.x, zoom), y: pxToMm(local.y, zoom) });

    useEditor.getState().beginEditing(
      layer.id,
      index === null ? undefined : { start: index, end: index }
    );
  }, [layer, zoom]);

  if (layer.hidden) return null;

  return (
    <Group
      ref={node}
      name={layer.id}
      {...frameOf(layer, zoom)}
      opacity={layer.opacity}
      listening={!layer.locked && !preview}
      draggable={!layer.locked && !preview}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDblClick={onDblClick}
      onDblTap={onDblClick}
    >
      <Drawing layer={layer} zoom={zoom} />
    </Group>
  );
}

/** The layer's own appearance, drawn at the origin of its group. */
function Drawing({ layer, zoom }: Props) {
  switch (layer.type) {
    case 'text':
      return <TextNode layer={layer} zoom={zoom} />;
    case 'shape':
      return <ShapeNode layer={layer} zoom={zoom} />;
    case 'group':
      return <GroupDrawing layer={layer} zoom={zoom} />;
    case 'image':
      return <ImageNode layer={layer} zoom={zoom} />;
    case 'graphic':
      return <GraphicNode layer={layer} zoom={zoom} />;
    case 'svg':
      return <PendingBox layer={layer} zoom={zoom} label="Graphic" />;
    case 'qr':
      return <QrNode layer={layer} zoom={zoom} />;
    case 'barcode':
      return <PendingBox layer={layer} zoom={zoom} label="Barcode" />;
    case 'placeholder':
      return <PlaceholderNode layer={layer} zoom={zoom} />;
    default:
      return null;
  }
}

/**
 * A graphic from the shop's library.
 *
 * The same paths the press file gets, drawn at this zoom: one Konva path per
 * path in the drawing, in the order they were written, so what is in front on
 * screen is what is in front on paper.
 */
function GraphicNode( { layer, zoom }: { layer: GraphicLayer; zoom: number } ) {
  const { width, height } = box( layer, zoom );

  return (
    <>
      {/*
        * Something to click on. A drawing is mostly thin lines with nothing
        * between them, and Konva only counts ink as hit: an icon could be
        * dragged nowhere except exactly along one of its strokes, and a
        * customer pressing in the middle of a telephone selected the paper
        * behind it. Every other kind of layer is picked up by its box, and so
        * is this one.
        */}
      <Rect width={ width } height={ height } fill="transparent" perfectDrawEnabled={ false } />

      { graphicPaths( layer ).map( ( path, index ) => (
        <Path
          key={ index }
          data={ scalePath( path.d, mmToPx( 1, zoom ), mmToPx( 1, zoom ) ) }
          fill={ path.fill && ! isGradient( path.fill ) ? toCss( path.fill ) : undefined }
          stroke={ path.stroke ? toCss( path.stroke ) : undefined }
          strokeWidth={ path.width ? mmToPx( path.width, zoom ) : 0 }
          fillRule={ layer.paths[ index ]?.evenOdd ? 'evenodd' : 'nonzero' }
          listening={ false }
          perfectDrawEnabled={ false }
        />
      ) ) }
    </>
  );
}

/** Width and height in screen pixels. Position is the group's job, not ours. */
function box(layer: Layer, zoom: number) {
  return { width: mmToPx(layer.w, zoom), height: mmToPx(layer.h, zoom) };
}

function ShapeNode({ layer, zoom }: { layer: ShapeLayer; zoom: number }) {
  const { width, height } = box(layer, zoom);
  const def = shapeDef(layer.shape);

  /*
   * The object's own paint, exactly as the press walk works it out (draw.ts).
   * A line has no inside, so its colour is its stroke - the stroke if it has
   * one, otherwise its fill's first colour - at 0.25 mm when it has no width
   * of its own, which is what prints.
   */
  const own = def.strokeOnly
    ? {
        fill: null,
        stroke: layer.stroke ? layer.stroke.colour : firstColour(layer.fill),
        width: layer.stroke ? layer.stroke.width : 0.25,
        dash: layer.stroke?.dash,
      }
    : {
        fill: layer.fill,
        stroke: layer.stroke ? layer.stroke.colour : null,
        width: layer.stroke ? layer.stroke.width : 0,
        dash: layer.stroke?.dash,
      };

  // The effect - or a design's old hard shadow - as the same passes the press
  // draws. The body is drawn by Konva below, which already fills gradients.
  const passes = plan(layer, own, { x: 0, y: 0, w: layer.w, h: layer.h }).map((pass) =>
    // A copy of a line is a line, in whatever colour the effect gives it.
    def.strokeOnly && !pass.body && pass.source === 'outline' && pass.fill && !pass.stroke
      ? { ...pass, stroke: firstColour(pass.fill), width: own.width, fill: null }
      : pass
  );
  const body = passes.find((pass) => pass.body) ?? { ...own, alpha: 1 };
  const effects = passes.some((pass) => !pass.body);
  const outline = [{ path: pathFor(shapePath(layer)), m: [1, 0, 0, 1, 0, 0] as Matrix }];

  /*
   * One generator, in pixels rather than millimetres.
   *
   * The library takes a box and returns a path in whatever unit the box is in,
   * so the canvas asks for pixels and the press walk asks for millimetres and
   * both get the same shape. Before this they were separate implementations,
   * and they had already drifted: an arrow was a stroked Konva.Arrow on screen
   * and a filled polygon in the press file.
   */
  const data = def.path(width, height, {
    radius: mmToPx(layer.radius, zoom),
    points: layer.points,
    // A drawn path carries its own geometry, and without these it would be
    // asked to draw itself from nothing and quietly produce an empty string.
    nodes: layer.nodes,
    closed: layer.closed,
  });

  /*
   * A gradient is given to Konva as its own kind of fill rather than as a CSS
   * string, because Konva paints onto a canvas and has no CSS to parse. The
   * geometry matches the two press sinks: an angle clockwise from
   * left-to-right, and a radial that fills the box rather than a circle inside
   * it - so what is on screen is what prints.
   */
  const fill = !body.fill
    ? { fillEnabled: false }
    : isGradient(body.fill)
      ? gradientProps(body.fill, width, height)
      : { fill: toCss(body.fill) };

  const stroke = body.stroke && body.width > 0
    ? {
        stroke: toCss(body.stroke),
        strokeWidth: mmToPx(body.width, zoom),
        dash: body.dash?.length ? body.dash.map((d) => mmToPx(d, zoom)) : undefined,
      }
    : {};

  return (
    <>
      {effects && (
        <Shape
          listening={false}
          sceneFunc={(context) => {
            const canvas = context._context;

            canvas.save();
            canvas.scale(mmToPx(1, zoom), mmToPx(1, zoom));
            paintPasses(canvas, passes, outline, { only: 'effects', line: def.strokeOnly ? own.width : undefined });
            canvas.restore();
          }}
        />
      )}
      <Path {...fill} {...stroke} data={data} />
    </>
  );
}

/** A gradient as the props Konva wants for it. */
function gradientProps(fill: Gradient, w: number, h: number) {
  const stops: Array<number | string> = [];

  for (const stop of fill.stops) {
    stops.push(Math.min(1, Math.max(0, stop.at)), toCss(stop.colour));
  }

  if (fill.kind === 'radial') {
    return {
      fillRadialGradientStartPoint: { x: w / 2, y: h / 2 },
      fillRadialGradientEndPoint: { x: w / 2, y: h / 2 },
      fillRadialGradientStartRadius: 0,
      // Konva's radial is a circle, so a wide box would show a circle with
      // corners left over. The larger half-dimension covers the box; the
      // slight difference from the ellipse the press file draws is the one
      // place the canvas is an approximation, and it is invisible at card size.
      fillRadialGradientEndRadius: Math.max(w, h) / 2,
      fillRadialGradientColorStops: stops,
    };
  }

  const radians = (fill.angle * Math.PI) / 180;
  const dx = Math.cos(radians) / 2;
  const dy = Math.sin(radians) / 2;

  return {
    fillLinearGradientStartPoint: { x: (0.5 - dx) * w, y: (0.5 - dy) * h },
    fillLinearGradientEndPoint: { x: (0.5 + dx) * w, y: (0.5 + dy) * h },
    fillLinearGradientColorStops: stops,
  };
}

/**
 * A group's children.
 *
 * Their positions are absolute on the page, and the group is placed at its own
 * corner, so each child is drawn at its offset from that corner.
 */
function GroupDrawing({ layer, zoom }: { layer: GroupLayer; zoom: number }) {
  return (
    <>
      {layer.children.map((child) => (
        /*
         * The same frame as a layer on its own, measured from the group's
         * corner. This was the last place still turning about the top-left and
         * ignoring flips, so a rotated or mirrored object inside a group sat in
         * one place on screen and printed in another - the bug frame.ts exists
         * to prevent, surviving one level down.
         */
        <Group
          key={child.id}
          name={child.id}
          {...frameOf({ ...child, x: child.x - layer.x, y: child.y - layer.y }, zoom)}
          opacity={child.opacity}
        >
          <Drawing layer={child} zoom={zoom} />
        </Group>
      ))}
    </>
  );
}

/**
 * A box for something the editor can hold but cannot yet draw.
 *
 * Deliberately plain and unmistakably unfinished: a dashed frame and a word.
 * It must not look like artwork, or a customer will order it.
 */
function PendingBox({
  layer,
  zoom,
  label,
}: {
  layer: ImageLayer | SvgLayer | QrLayer | BarcodeLayer;
  zoom: number;
  label: string;
}) {
  const { width, height } = box(layer, zoom);

  return (
    <>
      <Rect
        width={width}
        height={height}
        // Strong enough to find on white paper. The first version was faint
        // enough to be invisible on the card it sits on, which defeats the
        // whole point of drawing a box for something not there yet.
        fill="rgba(133, 136, 142, 0.14)"
        stroke="#6f737a"
        strokeWidth={1.25}
        dash={[5, 4]}
        strokeScaleEnabled={false}
      />
      <Text
        width={width}
        height={height}
        text={label}
        align="center"
        verticalAlign="middle"
        fill="#5c6067"
        fontSize={Math.max(9, Math.min(13, height / 4))}
        fontFamily="system-ui, sans-serif"
        listening={false}
      />
    </>
  );
}

/**
 * A field waiting to be filled in.
 *
 * Marked with the accent, because unlike a picture box this one is a question
 * being asked of the customer and should be findable at a glance.
 */
function PlaceholderNode({ layer, zoom }: { layer: PlaceholderLayer; zoom: number }) {
  const { width, height } = box(layer, zoom);
  const label = layer.prompt || layer.placeholder;

  return (
    <>
      <Rect
        width={width}
        height={height}
        fill="rgba(221, 29, 38, 0.06)"
        stroke="#dd1d26"
        strokeWidth={1}
        dash={[4, 3]}
        strokeScaleEnabled={false}
        cornerRadius={3}
      />
      <Text
        width={width}
        height={height}
        text={label}
        align="center"
        verticalAlign="middle"
        fill="#dd1d26"
        fontSize={Math.max(9, Math.min(13, height / 3))}
        fontFamily="system-ui, sans-serif"
        listening={false}
      />
      {layer.expects === 'image' && <Circle x={width - 10} y={10} radius={3} fill="#dd1d26" listening={false} />}
    </>
  );
}
